/*
 * cross-sell-popup.js
 * Global PDP cross-sell popup trigger. Pairs with snippets/cross-sell-popup.liquid
 * and the theme settings group "Cross-sell popup".
 *
 * How it works
 *  - Only products that have a configured cross-sell render the popup + get their
 *    main add button marked [data-cross-sell-source] with xo-cart-opened="false", so
 *    the web component does NOT auto-open the mini-cart for those. This script owns
 *    opening the mini-cart in every path (so behaviour for other products is
 *    untouched — if this script never runs, they add + open the drawer as before).
 *  - On a successful source add we evaluate the cross-sell ladder against LIVE cart
 *    contents (/cart.js) plus the two pre-rendered candidate cards, then either
 *    reveal the right card + open the cross-sell modal, or just open the mini-cart.
 *  - Any dismissal of the modal (No thanks / X / backdrop / ESC) opens the mini-cart.
 *  - Adding the shown cross-sell (its own <xo-cart-add>) closes the popup; that add's
 *    own logic opens the mini-cart (subject to the "open mini-cart" setting).
 *  - Shown at most once per browsing session (sessionStorage).
 */
(function () {
  'use strict';

  var SESSION_KEY = 'xoCrossSellShown';
  var MODAL_NAME = 'cross-sell';
  var CART_MODAL_NAME = 'cart';

  function getStore() { return window.xoStore; }
  function getModal() { return window.xoModal; }

  function openMiniCart() {
    var root = document.querySelector('[data-cross-sell-root]');
    // Respect the "open mini-cart after" setting.
    if (root && root.getAttribute('data-open-mini-cart') === 'false') return;
    var modal = getModal();
    if (modal && typeof modal.open === 'function') modal.open(CART_MODAL_NAME);
  }

  function alreadyShownThisSession() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; }
  }
  function markShownThisSession() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
  }

  // Resolves to a map { productId: true } of products currently in the cart.
  function fetchCartProductIds() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .then(function (cart) {
        var ids = {};
        (cart.items || []).forEach(function (it) { ids[String(it.product_id)] = true; });
        return ids;
      })
      .catch(function () { return {}; });
  }

  // Ladder: primary first, then fallback. Skip a candidate that is the product just
  // added, is sold out, or is already in the cart. Returns the card element or null.
  function pickCandidate(root, cartIds) {
    var sourceId = root.getAttribute('data-source-id');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-cross-sell-card]'));
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var pid = card.getAttribute('data-product-id');
      if (!pid) continue;
      if (pid === sourceId) continue;                       // same product just added
      if (card.getAttribute('data-available') !== 'true') continue; // sold out
      if (cartIds[pid]) continue;                           // already in cart
      return card;
    }
    return null;
  }

  function revealCard(root, card) {
    var cards = root.querySelectorAll('[data-cross-sell-card]');
    Array.prototype.forEach.call(cards, function (c) {
      if (c === card) c.removeAttribute('hidden');
      else c.setAttribute('hidden', '');
    });
  }

  var handling = false;         // guards one in-flight decision
  var crossSellResolved = false; // set when the popup is closed by adding the cross-sell

  // Safety net: the theme makes a modal interactive only when its open animation
  // reaches its final frame. If that ever stalls, the popup would render but stay
  // non-interactive (pointer-events:none). Once the modal is open, make sure it is
  // clickable. No-op when the animation completes normally.
  function ensureInteractive() {
    var store = getStore();
    var m = document.querySelector('[data-cross-sell-root]');
    if (!store || !m) return;
    var d = store.get('xo-modal');
    var isOpen = !!(d && d.data && d.data[MODAL_NAME] && d.data[MODAL_NAME].isOpen);
    if (!isOpen) return;
    if (getComputedStyle(m).pointerEvents === 'none') {
      m.style.pointerEvents = 'auto';
      if (m.style.zIndex === '-1' || m.style.zIndex === '') m.style.zIndex = '10000';
    }
  }

  function handleSourceAdd() {
    if (handling) return;
    var root = document.querySelector('[data-cross-sell-root]');
    if (!root) { openMiniCart(); return; }
    if (alreadyShownThisSession()) { openMiniCart(); return; }

    handling = true;
    fetchCartProductIds().then(function (cartIds) {
      var card = pickCandidate(root, cartIds);
      if (!card) { openMiniCart(); handling = false; return; }
      revealCard(root, card);
      markShownThisSession();
      crossSellResolved = false;
      var modal = getModal();
      if (modal && typeof modal.open === 'function') {
        // Pass a real, on-page element as the open origin so the modal's open
        // animation/interactivity does not depend on a trigger inside the modal.
        var origin = document.querySelector('[data-cross-sell-source]') || undefined;
        modal.open(MODAL_NAME, origin);
        setTimeout(ensureInteractive, 400);
        setTimeout(ensureInteractive, 800);
      } else {
        openMiniCart();
      }
      handling = false;
    });
  }

  // --- click wiring (capture phase, before the web component's own handlers) ---
  var pendingSource = false;

  function isCrossSellOpen() {
    var store = getStore();
    if (!store) return false;
    var d = store.get('xo-modal');
    return !!(d && d.data && d.data[MODAL_NAME] && d.data[MODAL_NAME].isOpen);
  }
  function closeCrossSell() {
    var modal = getModal();
    if (modal && typeof modal.close === 'function') modal.close(MODAL_NAME);
  }

  document.addEventListener(
    'click',
    function (event) {
      var t = event.target;
      if (!t || !t.closest) return;

      // Main PDP add for a cross-sell product: remember to evaluate on success.
      if (t.closest('[data-cross-sell-source]')) {
        pendingSource = true;
      }

      // The rest only applies while the popup is open.
      if (!isCrossSellOpen()) return;

      // Adding the shown cross-sell: close the popup; its own add opens the mini-cart.
      // Do NOT stop propagation here — the card's own <xo-cart-add> must still run.
      if (t.closest('[data-cross-sell-add]')) {
        crossSellResolved = true;
        closeCrossSell();
        return;
      }

      // Every dismissal path routed through our own close (the same call the working
      // Add-to-cart uses), so it never depends on the theme's modal-trigger toggle:
      //   - the close (X), the "No thanks" link, or any click outside the card.
      var root = document.querySelector('[data-cross-sell-root]');
      var inner = root && root.querySelector('.xo-modal-content__inner');
      var isCloseControl =
        t.closest('.xo-modal-content__close') || t.closest('.xo-cross-sell__decline-btn');
      var outsideCard = inner && !inner.contains(t);
      if (isCloseControl || outsideCard) {
        // Stop the theme's own trigger/backdrop from also toggling (which would reopen it).
        event.preventDefault();
        event.stopImmediatePropagation();
        crossSellResolved = false; // dismissal -> subscribeModal opens the mini-cart
        closeCrossSell();
      }
    },
    true
  );

  // --- cart store: detect a completed source add ---
  var prevIsAdded = false;

  function subscribeCart(store) {
    store.subscribe('xo-cart', function (state) {
      if (!state) return;
      var isAdded = !!state.isAdded;
      if (state.addErrorMessage && state.addErrorMessage !== '') {
        pendingSource = false;
        prevIsAdded = isAdded;
        return;
      }
      if (isAdded && !prevIsAdded && pendingSource) {
        pendingSource = false;
        handleSourceAdd();
      }
      prevIsAdded = isAdded;
    });
  }

  // --- modal store: any dismissal of the popup opens the mini-cart ---
  function subscribeModal(store) {
    var prevOpen = false;
    store.subscribe('xo-modal', function (state) {
      var isOpen = !!(state && state.data && state.data[MODAL_NAME] && state.data[MODAL_NAME].isOpen);
      if (prevOpen && !isOpen) {
        // Defer to a fresh task: opening the mini-cart writes the same modal store we
        // are currently inside a listener for. Doing it synchronously is a re-entrant
        // store write that crashes the theme (and aborted our close). setTimeout breaks
        // out of this update cascade so both the close and the mini-cart open cleanly.
        var wasResolved = crossSellResolved;
        crossSellResolved = false;
        if (!wasResolved) setTimeout(openMiniCart, 0);
      }
      prevOpen = isOpen;
    });
  }

  // xoStore is created by the web-components bundle; it may not exist yet. Retry briefly.
  function init(attempt) {
    var store = getStore();
    if (store && typeof store.subscribe === 'function') {
      subscribeCart(store);
      subscribeModal(store);
      return;
    }
    if (attempt < 50) setTimeout(function () { init(attempt + 1); }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(0); });
  } else {
    init(0);
  }
})();
